import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, DatePicker, message, Select, Table, Tabs } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import axios from 'axios';
import moment from 'moment-timezone';

import { ITransferRequest, useTransferRequestList } from 'api/bdo/transfer-requests';
import { SearchBar, WrapperPanel } from 'components';
import { FlexContainer } from 'components/Layout/common';
import { PageContent } from 'components/page';
import { useListState } from 'hooks';
import { formatNumber } from 'utils/formatters';
import API, { downloadFile } from 'utils/request';

import { dateTimeFormat, serverDateFormat } from '../constants';
import RequestStatusTag from '../transfer-requests/components/RequestStatusTag';
import { getCardTypeByName } from '../utils';

const { RangePicker } = DatePicker;

interface AmexDinersPointsRedemptionProps {
  activeTab: string;
  canConfigure?: boolean;
}

const AmexDinersPointsRedemption: React.FC<AmexDinersPointsRedemptionProps> = ({
  activeTab,
  canConfigure = true
}) => {
  const { handleSearch, handleStateChange, handleTableChange, pagination, params } = useListState({
    state: 'new'
  });

  const [dateRange, setDateRange] = useState(null);
  const [requestType, setRequestType] = useState<string[]>();
  const [productCodeOptions, setProductCodeOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [selectedProductCode, setSelectedProductCode] = useState<string[]>();

  const [startDate, endDate] = dateRange ?? [];
  const start_date = startDate ? moment(startDate).format(serverDateFormat) : null;
  const end_date = endDate ? moment(endDate).format(serverDateFormat) : null;

  const requestTypeOptions = [
    { label: 'Airmiles', value: 'airmile' },
    { label: 'Mem. Fee Waiver', value: 'membership_fee' }
  ];

  if (activeTab === 'amex') {
    requestTypeOptions.push({ label: 'Hotel Loyalty', value: 'hotel_loyalty' });
    requestTypeOptions.push({ label: 'Vouchers', value: 'evoucher,paper' });
  }
  if (activeTab === 'diners') {
    requestTypeOptions.push({ label: 'Cash Credit', value: 'cash_credit' });
  }

  useEffect(() => {
    const fetchProductCode = async () => {
      if (!canConfigure) {
        setProductCodeOptions([]);
        return;
      }

      if (activeTab === 'amex' || activeTab === 'diners') {
        try {
          const res = await API.get(
            `/v4/dash/custom/bdo/transfer_requests/cards/${activeTab}/product_codes`
          );
          setProductCodeOptions(
            res?.data?.product_codes?.map((pt: any) => ({ label: pt, value: pt })) || []
          );
        } catch (error) {
          if (!(axios.isAxiosError(error) && error.response?.status === 403)) {
            message.error('Failed to load product types');
          }
          setProductCodeOptions([]);
        }
      } else {
        setProductCodeOptions([]);
      }
    };

    fetchProductCode();
  }, [activeTab, canConfigure]);

  const { data, meta, isLoading } = useTransferRequestList(
    {
      ...params,
      card_type: activeTab,
      start_date,
      end_date,
      request_type: requestType?.join(',')
    },
    selectedProductCode?.map(productType => `product_code[]=${productType}`).join('&')
  );

  const columns = useMemo(() => {
    const arr: ColumnProps<ITransferRequest>[] = [
      {
        title: 'Request No.',
        key: 'id',
        render: (_, record) => (
          <Link to={`/p/points_redemptions/show/${record?.id}`}>{record?.id}</Link>
        )
      },
      {
        title: 'Created On',
        key: 'src_brc',
        render: (_, record) => moment(record?.created_at).format(dateTimeFormat)
      },
      {
        title: 'Name',
        key: 'name',
        width: 250,
        render: (_, record) =>
          [record?.properties?.sender_first_name, record?.properties?.sender_last_name]
            .filter(Boolean)
            .join(' ')
            .slice(0, 35)
      },
      {
        title: 'Card No.',
        key: 'card_no',
        width: 250,
        render: (_, record) => record?.properties?.sender
      },
      {
        title: 'Card Type',
        key: 'card_type',
        width: 250,
        render: (_, record) => record?.properties?.product_code
      },
      {
        title: 'Destination',
        key: 'destination',
        render: (_, record) => {
          let destination = '-';
          if (record?.request_type === 'hotel_loyalty' || record?.request_type === 'airmile')
            destination = record?.properties?.account_number;
          if (record?.request_type === 'cash_credit') destination = record?.properties?.sender;
          if (record?.request_type === 'membership_fee')
            destination = record?.supplementary ? 'Supplementary' : 'Principal';
          return destination;
        }
      },
      {
        title: 'Redeem Type',
        key: 'redeem_type',
        render: (_, record) => getCardTypeByName(record?.request_type)
      },
      {
        title: 'LRS Code',
        key: 'lrs_code',
        render: (_, record) => record?.properties?.lrsRewardCode
      },
      {
        title: 'Amount',
        key: 'amount',
        render: (_, record) => formatNumber(Number(record?.properties?.amount ?? 0) / 100)
      },
      ...(params.state !== 'new'
        ? [
            {
              title: 'Status',
              key: 'status',
              render: (_, record) => <RequestStatusTag state={record.state} />
            }
          ]
        : [])
    ];

    return arr;
  }, [params.state]);

  const downloadCSV = async () => {
    try {
      const response = await API.get(
        `/v4/dash/custom/bdo/point_transfers.csv${
          selectedProductCode?.length
            ? `?${selectedProductCode
                .map(productType => `product_code[]=${productType}`)
                .join('&')}`
            : ''
        }`,
        {
          params: {
            start_date,
            end_date,
            ...params,
            card_type: activeTab,
            request_type: requestType?.join(',')
          },
          headers: { Accept: '*/*', 'Content-Type': 'text/csv' }
        }
      );

      let fileName = 'transfer-requests';
      if (start_date) fileName += `-from-${start_date}`;
      if (end_date) fileName += `-to-${end_date}`;

      downloadFile(response.data, `${fileName}.csv`);
    } catch (error) {
      if (!axios.isCancel(error)) {
        message.error(error?.message || 'Something went wrong');
      }
    }
  };

  const downloadSummary = async () => {
    try {
      const { page, size, ...restParams } = params;
      const response = await API.get(
        `/v4/dash/custom/bdo/point_transfers/report${
          selectedProductCode?.length
            ? `?${selectedProductCode
                .map(productType => `product_code[]=${productType}`)
                .join('&')}`
            : ''
        }`,
        {
          params: {
            start_date,
            end_date,
            ...restParams,
            card_type: activeTab,
            request_type: requestType?.join(',')
          },
          headers: { Accept: '*/*', 'Content-Type': 'text/csv' }
        }
      );

      const stateMap = {
        new: 'New',
        completed: 'Complete',
        declined: 'Declined',
        cancelled: 'Cancelled',
        in_progress: 'In_progress'
      };
      const tabPrefix = activeTab === 'amex' ? 'AMEX' : 'Diners';
      const stateLabel = stateMap[params.state] || params.state;

      let fileName = `${tabPrefix}_${stateLabel}_points_summary`;
      if (start_date) fileName += `-from-${start_date}`;
      if (end_date) fileName += `-to-${end_date}`;

      downloadFile(response.data, `${fileName}.csv`);
    } catch (error) {
      if (!axios.isCancel(error)) {
        message.error(error?.message || 'Something went wrong');
      }
    }
  };

  const tabsItems = [
    { key: 'new', label: 'New' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'declined', label: 'Declined' },
    { key: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <>
      <PageContent>
        <WrapperPanel>
          <FlexContainer
            justifyContent="space-between"
            alignItems="flex-start"
            style={{ rowGap: '10px' }}
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <SearchBar
                style={{ width: 250 }}
                onSearch={handleSearch}
                placeholder="Search Request"
              />
              <Select
                placeholder="Filter by request type"
                allowClear
                style={{ minWidth: 300, maxWidth: 800 }}
                size="large"
                mode="multiple"
                onChange={value => {
                  setRequestType(value);
                  handleTableChange({ current: 1, pageSize: pagination.pageSize }, {}, {});
                }}
                options={requestTypeOptions}
                value={requestType}
              />
              <Select
                placeholder="Filter by card product type"
                allowClear
                style={{ minWidth: 300, maxWidth: 800 }}
                size="large"
                mode="multiple"
                options={productCodeOptions}
                value={selectedProductCode}
                onChange={(values: string[]) => {
                  setSelectedProductCode(values);
                  handleTableChange({ current: 1, pageSize: pagination.pageSize }, {}, {});
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <RangePicker
                value={dateRange}
                onChange={value => {
                  setDateRange(value);
                  handleTableChange({ current: 1, pageSize: pagination.pageSize }, {}, {});
                }}
                size="large"
              />
              <Button size="large" onClick={downloadSummary}>
                Download Summary
              </Button>
              <Button size="large" onClick={downloadCSV}>
                Download CSV
              </Button>
            </div>
          </FlexContainer>
          <Tabs
            activeKey={params.state}
            onChange={handleStateChange}
            tabBarStyle={{ marginBottom: 0 }}
            items={tabsItems}
          />
          <Table<ITransferRequest>
            rowKey="id"
            dataSource={data}
            columns={columns}
            pagination={{
              ...pagination,
              total: meta?.total_count
            }}
            loading={isLoading}
            onChange={handleTableChange}
          />
        </WrapperPanel>
      </PageContent>
    </>
  );
};

export default AmexDinersPointsRedemption;
